<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AiAgentsAPI from 'dashboard/api/aiAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useAlert } from 'dashboard/composables';
import {
  aiAgentToForm,
  blankDocument,
  buildAiAgentPayload,
  emptyAiAgentForm,
  validateAiAgentForm,
} from './aiAgentUtils';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  labels: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);
const { t } = useI18n();

const panelRef = ref(null);
const agentId = ref(null);
const form = ref(emptyAiAgentForm());
const touched = ref(false);
const isSubmitting = ref(false);
const removedDocumentIds = ref([]);

const title = computed(() =>
  agentId.value ? t('AI_AGENT.FORM.EDIT_TITLE') : t('AI_AGENT.FORM.NEW_TITLE')
);

const errors = computed(() =>
  touched.value ? validateAiAgentForm(form.value) : {}
);

const isValid = computed(
  () => Object.keys(validateAiAgentForm(form.value)).length === 0
);

const inboxOptions = computed(() => [
  { value: '', label: t('AI_AGENT.FORM.INBOX_ALL') },
  ...props.inboxes.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  })),
]);

const labelOptions = computed(() =>
  props.labels.map(label => ({ value: label.title, label: label.title }))
);

const hourError = field => {
  if (errors.value[field] === 'range') return t('AI_AGENT.FORM.HOURS_RANGE');
  if (errors.value[field] === 'paired') return t('AI_AGENT.FORM.HOURS_PAIRED');
  return '';
};

const open = agent => {
  form.value = agent ? aiAgentToForm(agent) : emptyAiAgentForm();
  agentId.value = agent?.id || null;
  removedDocumentIds.value = [];
  touched.value = false;
  panelRef.value?.open();
};

const close = () => panelRef.value?.close();

const addDocument = () => form.value.documents.push(blankDocument());

const removeDocument = index => {
  const [removed] = form.value.documents.splice(index, 1);
  if (removed?.id) removedDocumentIds.value.push(removed.id);
};

const submit = async () => {
  touched.value = true;
  if (!isValid.value) return;

  isSubmitting.value = true;
  try {
    const payload = buildAiAgentPayload(form.value, removedDocumentIds.value);
    if (agentId.value) await AiAgentsAPI.updateAgent(agentId.value, payload);
    else await AiAgentsAPI.createAgent(payload);
    useAlert(t('AI_AGENT.FORM.SAVED'));
    emit('saved');
    close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('AI_AGENT.FORM.SAVE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <SidePanel
    ref="panelRef"
    width="2xl"
    :title="title"
    :description="$t('AI_AGENT.FORM.DESCRIPTION')"
  >
    <form class="flex flex-col gap-6" @submit.prevent="submit">
      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('AI_AGENT.FORM.SECTIONS.GENERAL') }}
        </h3>
        <Input
          v-model="form.name"
          :label="$t('AI_AGENT.FORM.NAME')"
          :placeholder="$t('AI_AGENT.FORM.NAME_PLACEHOLDER')"
          :message="errors.name ? $t('AI_AGENT.FORM.NAME_REQUIRED') : ''"
          :message-type="errors.name ? 'error' : 'info'"
          autocomplete="off"
        />
        <label
          class="flex items-center justify-between gap-2 text-sm text-n-slate-12"
        >
          {{ $t('AI_AGENT.FORM.ACTIVE') }}
          <Switch v-model="form.active" />
        </label>
        <label
          class="flex items-center justify-between gap-2 text-sm text-n-slate-12"
        >
          {{ $t('AI_AGENT.FORM.AUTO_REPLY') }}
          <Switch v-model="form.autoReply" />
        </label>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('AI_AGENT.FORM.SECTIONS.INSTRUCTIONS') }}
        </h3>
        <TextArea
          v-model="form.instructions"
          :label="$t('AI_AGENT.FORM.INSTRUCTIONS')"
          :placeholder="$t('AI_AGENT.FORM.INSTRUCTIONS_PLACEHOLDER')"
          auto-height
        />
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('AI_AGENT.FORM.SECTIONS.AUDIENCE') }}
        </h3>
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ $t('AI_AGENT.FORM.INBOX') }}
          <Select
            v-model="form.inboxId"
            class="!w-full [&>select]:w-full"
            :options="inboxOptions"
          />
        </label>
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="form.activeFromHour"
            type="number"
            :label="$t('AI_AGENT.FORM.ACTIVE_FROM_HOUR')"
            :placeholder="$t('AI_AGENT.FORM.HOUR_PLACEHOLDER')"
            :message="hourError('activeFromHour')"
            :message-type="errors.activeFromHour ? 'error' : 'info'"
          />
          <Input
            v-model="form.activeToHour"
            type="number"
            :label="$t('AI_AGENT.FORM.ACTIVE_TO_HOUR')"
            :placeholder="$t('AI_AGENT.FORM.HOUR_PLACEHOLDER')"
            :message="hourError('activeToHour')"
            :message-type="errors.activeToHour ? 'error' : 'info'"
          />
        </div>
        <div class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ $t('AI_AGENT.FORM.AUDIENCE_LABELS') }}
          <TagMultiSelectComboBox
            v-model="form.audienceLabels"
            :options="labelOptions"
            :placeholder="$t('AI_AGENT.FORM.AUDIENCE_LABELS_PLACEHOLDER')"
            :search-placeholder="$t('AI_AGENT.FORM.AUDIENCE_LABELS_SEARCH')"
            :empty-state="$t('AI_AGENT.FORM.AUDIENCE_LABELS_EMPTY')"
            :message="$t('AI_AGENT.FORM.AUDIENCE_LABELS_HINT')"
          />
        </div>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('AI_AGENT.FORM.SECTIONS.HANDOFF') }}
        </h3>
        <div class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ $t('AI_AGENT.FORM.FORBIDDEN_TOPICS') }}
          <TagInput
            v-model="form.forbiddenTopics"
            :placeholder="$t('AI_AGENT.FORM.FORBIDDEN_TOPICS_PLACEHOLDER')"
            allow-create
          />
        </div>
        <Input
          v-model="form.maxMessagesPerConversation"
          type="number"
          :label="$t('AI_AGENT.FORM.MAX_MESSAGES')"
          :message="$t('AI_AGENT.FORM.MAX_MESSAGES_HINT')"
          :message-type="errors.maxMessagesPerConversation ? 'error' : 'info'"
        />
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('AI_AGENT.FORM.SECTIONS.FALLBACK') }}
        </h3>
        <Input
          v-model="form.fallbackTemplateName"
          :label="$t('AI_AGENT.FORM.FALLBACK_TEMPLATE')"
          :placeholder="$t('AI_AGENT.FORM.FALLBACK_TEMPLATE_PLACEHOLDER')"
          :message="$t('AI_AGENT.FORM.FALLBACK_TEMPLATE_HINT')"
          autocomplete="off"
        />
        <div class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ $t('AI_AGENT.FORM.FALLBACK_VARIABLES') }}
          <TagInput
            v-model="form.fallbackTemplateVariables"
            :placeholder="$t('AI_AGENT.FORM.FALLBACK_VARIABLES_PLACEHOLDER')"
            allow-create
          />
        </div>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('AI_AGENT.FORM.SECTIONS.DOCUMENTS') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('AI_AGENT.FORM.DOCUMENTS_HINT') }}
        </p>
        <div
          v-for="(document, index) in form.documents"
          :key="index"
          class="flex flex-col gap-3 p-3 border rounded-xl border-n-weak"
        >
          <Input
            v-model="document.title"
            :label="$t('AI_AGENT.FORM.DOCUMENT_TITLE')"
            :message="
              errors[`document-${index}`]
                ? $t('AI_AGENT.FORM.DOCUMENT_TITLE_REQUIRED')
                : ''
            "
            :message-type="errors[`document-${index}`] ? 'error' : 'info'"
          />
          <TextArea
            v-model="document.body"
            :label="$t('AI_AGENT.FORM.DOCUMENT_BODY')"
            :placeholder="$t('AI_AGENT.FORM.DOCUMENT_BODY_PLACEHOLDER')"
            auto-height
          />
          <Button
            type="button"
            size="sm"
            color="slate"
            icon="i-woot-bin"
            :label="$t('AI_AGENT.FORM.REMOVE_DOCUMENT')"
            class="self-start"
            @click="removeDocument(index)"
          />
        </div>
        <Button
          type="button"
          size="sm"
          color="slate"
          icon="i-lucide-plus"
          :label="$t('AI_AGENT.FORM.ADD_DOCUMENT')"
          class="self-start"
          @click="addDocument"
        />
      </section>

      <footer class="flex items-center justify-end gap-2">
        <Button
          type="button"
          faded
          slate
          :label="$t('AI_AGENT.FORM.CANCEL')"
          @click="close"
        />
        <Button
          type="submit"
          :label="$t('AI_AGENT.FORM.SAVE')"
          :is-loading="isSubmitting"
        />
      </footer>
    </form>
  </SidePanel>
</template>
